import { agreementTemplateFormToQuery } from 'middleware/templates/agreement-template/agreementTemplateFormToQuery';
import { IAgreementTemplatetAggregatorForm } from 'services/templates/agreement-template/types';
import {
  CUSTOM_SECTION_TYPE,
  ICustomSectionTemplateMutation,
  ICustomSectionType,
} from 'services/templates/section-template/types';
import {
  IContractTemplateTreeItemForm,
  TEMPLATE_TYPE,
} from 'services/templates/types';

const hasSectionContent = (section: ICustomSectionTemplateMutation) =>
  section.agreements.length > 0 || section.customSections.length > 0;

const mapSectionChildren = (
  children: IContractTemplateTreeItemForm[]
): Pick<ICustomSectionTemplateMutation, 'agreements' | 'customSections'> => {
  const agreements: ICustomSectionTemplateMutation['agreements'] = [];
  const customSections: ICustomSectionTemplateMutation['customSections'] = [];
  let position = 0;

  for (const child of children) {
    if (child.type === TEMPLATE_TYPE.agreement) {
      agreements.push(
        agreementTemplateFormToQuery(
          child.content as IAgreementTemplatetAggregatorForm,
          position,
          true
        )
      );
      position += 1;
      continue;
    }

    if (child.type === TEMPLATE_TYPE.section) {
      const mapped = mapSectionTreeItem(
        child as IContractTemplateTreeItemForm & { content: string },
        CUSTOM_SECTION_TYPE.Text,
        position
      );

      if (hasSectionContent(mapped)) {
        customSections.push(mapped);
        position += 1;
      }
    }
  }

  return { agreements, customSections };
};

const mapSectionTreeItem = (
  item: IContractTemplateTreeItemForm & { content: string },
  type: CUSTOM_SECTION_TYPE,
  position: number
): ICustomSectionTemplateMutation => ({
  title: item.content,
  position,
  type,
  ...mapSectionChildren(item.children),
});

export const mapCustomSectionsFromItems = (
  items: IContractTemplateTreeItemForm[]
): ICustomSectionTemplateMutation[] => {
  const sections: ICustomSectionTemplateMutation[] = [];

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];

    if (
      item.type !== TEMPLATE_TYPE.section &&
      item.type !== TEMPLATE_TYPE.paragraphSection
    ) {
      continue;
    }

    sections.push(
      mapSectionTreeItem(
        item as IContractTemplateTreeItemForm & { content: string },
        item.type === TEMPLATE_TYPE.paragraphSection
          ? CUSTOM_SECTION_TYPE.Paragraph
          : CUSTOM_SECTION_TYPE.Text,
        index
      )
    );
  }

  return sections;
};

export const parseCustomSectionType = (
  type?: ICustomSectionType
): CUSTOM_SECTION_TYPE => {
  if (type === undefined || type === null) {
    return CUSTOM_SECTION_TYPE.Text;
  }

  if (typeof type === 'number') {
    return type === CUSTOM_SECTION_TYPE.Paragraph
      ? CUSTOM_SECTION_TYPE.Paragraph
      : CUSTOM_SECTION_TYPE.Text;
  }

  if (typeof type === 'object' && 'id' in type) {
    return type.id === CUSTOM_SECTION_TYPE.Paragraph
      ? CUSTOM_SECTION_TYPE.Paragraph
      : CUSTOM_SECTION_TYPE.Text;
  }

  return CUSTOM_SECTION_TYPE.Text;
};

export const getCustomSectionTemplateType = (
  type?: ICustomSectionType
): CUSTOM_SECTION_TYPE => parseCustomSectionType(type);

export const isParagraphCustomSection = (type?: ICustomSectionType) =>
  parseCustomSectionType(type) === CUSTOM_SECTION_TYPE.Paragraph;
